import { Notification as AppNotification } from '@app/model/notification';

export type NotificationRoute = string | string[] | null;

export type NotificationRule = {
  label: string;
  icon: string;
  bgClass: string;
  statusClass: string;
  route: (notification: AppNotification) => NotificationRoute;
  readBeforeNavigate?: boolean;
};

function hasBoundId(notification: AppNotification): boolean {
  return Boolean(notification.bound_id && String(notification.bound_id).trim());
}

function taskDetailRoute(notification: AppNotification): NotificationRoute {
  return hasBoundId(notification) ? ['task-detail', notification.bound_id] : null;
}

function taskOperatorRoute(notification: AppNotification): NotificationRoute {
  return hasBoundId(notification) ? ['task-detail-operator', notification.bound_id] : null;
}

export const notificationRules: Record<string, NotificationRule> = {
  task_created: {
    label: '新任務',
    icon: 'bi-plus-square-fill',
    bgClass: 'bg-app-brand-soft text-app-brand-dark',
    statusClass: 'bg-app-brand',
    route: taskDetailRoute
  },
  task_match_list_update: {
    label: '配對更新',
    icon: 'bi-people-fill',
    bgClass: 'bg-app-highlight-tint text-app-highlight-text',
    statusClass: 'bg-app-highlight',
    route: taskOperatorRoute
  },
  task_accepted: {
    label: '任務接受',
    icon: 'bi-check-circle-fill',
    bgClass: 'bg-app-supply-soft text-app-supply-dark',
    statusClass: 'bg-app-supply',
    route: taskDetailRoute,
    readBeforeNavigate: true
  },
  task_denied: {
    label: '任務拒絕',
    icon: 'bi-x-circle-fill',
    bgClass: 'bg-app-accent-soft text-app-accent-dark',
    statusClass: 'bg-app-accent',
    route: taskDetailRoute,
    readBeforeNavigate: true
  },
  task_expired: {
    label: '任務過期',
    icon: 'bi-clock-fill',
    bgClass: 'bg-app-highlight-tint text-app-highlight-text',
    statusClass: 'bg-app-highlight',
    route: () => null,
    readBeforeNavigate: true
  },
  finish_task: {
    label: '任務完成',
    icon: 'bi-flag-fill',
    bgClass: 'bg-app-info-soft text-app-info-dark',
    statusClass: 'bg-app-info',
    route: () => 'create-task-history',
    readBeforeNavigate: true
  },
  complete_task: {
    label: '任務結束',
    icon: 'bi-check2-all',
    bgClass: 'bg-app-supply-soft text-app-supply-dark',
    statusClass: 'bg-app-supply',
    route: taskDetailRoute
  },
  delete_task: {
    label: '任務刪除',
    icon: 'bi-trash3-fill',
    bgClass: 'bg-app-accent-soft text-app-accent-dark',
    statusClass: 'bg-app-accent',
    route: () => null
  },
  refund_task: {
    label: '退款通知',
    icon: 'bi-arrow-counterclockwise',
    bgClass: 'bg-app-highlight-tint text-app-highlight-text',
    statusClass: 'bg-app-highlight',
    route: () => 'finance-transfer-history'
  },
  system_notice: {
    label: '系統通知',
    icon: 'bi-info-square-fill',
    bgClass: 'bg-app-info-soft text-app-info-dark',
    statusClass: 'bg-app-info',
    route: () => null
  },
  identity_apply: {
    label: '身份申請',
    icon: 'bi-person-badge-fill',
    bgClass: 'bg-app-info-soft text-app-info-dark',
    statusClass: 'bg-app-info',
    route: () => 'admin-manage-identity-apply',
    readBeforeNavigate: true
  },
  category_apply: {
    label: '類別申請',
    icon: 'bi-ui-checks',
    bgClass: 'bg-app-brand-soft text-app-brand-dark',
    statusClass: 'bg-app-brand',
    route: () => 'admin-manage-apply',
    readBeforeNavigate: true
  },
  apply_success: {
    label: '申請成功',
    icon: 'bi-patch-check-fill',
    bgClass: 'bg-app-supply-soft text-app-supply-dark',
    statusClass: 'bg-app-supply',
    route: () => 'apply-schedule',
    readBeforeNavigate: true
  },
  apply_unsuccess: {
    label: '申請失敗',
    icon: 'bi-exclamation-circle-fill',
    bgClass: 'bg-app-accent-soft text-app-accent-dark',
    statusClass: 'bg-app-accent',
    route: () => 'apply-schedule',
    readBeforeNavigate: true
  },
  announcement: {
    label: '公告',
    icon: 'bi-megaphone-fill',
    bgClass: 'bg-app-brand-soft text-app-brand-dark',
    statusClass: 'bg-app-brand',
    route: () => null
  },
  transfer_in: {
    label: '轉入',
    icon: 'bi-arrow-down-left-square-fill',
    bgClass: 'bg-app-supply-soft text-app-supply-dark',
    statusClass: 'bg-app-supply',
    route: () => 'finance-transfer-history'
  },
  transfer_out: {
    label: '轉出',
    icon: 'bi-arrow-up-right-square-fill',
    bgClass: 'bg-app-accent-soft text-app-accent-dark',
    statusClass: 'bg-app-accent',
    route: () => 'finance-transfer-history'
  }
};

export const defaultNotificationRule: NotificationRule = {
  label: '通知',
  icon: 'bi-bell-fill',
  bgClass: 'bg-app-info-soft text-app-info-dark',
  statusClass: 'bg-app-info',
  route: () => null
};

export function getNotificationRule(type: string | null | undefined): NotificationRule {
  return notificationRules[type ?? ''] ?? defaultNotificationRule;
}

export function getNotificationRoute(notification: AppNotification): NotificationRoute {
  return getNotificationRule(notification.notification_type).route(notification);
}
